import getById from 'utils/DOM/getById'
import { hideLoading } from 'FI/consumer/quotes/utils/utils'
import executeWhenFinishToRender from 'core/renders/executeWhenFinishToRender'
import Templater from '@hc-frontend/shared-assets/src/js/utils/dom/Templater'
import moneyFormat from 'utils/format/money'
import { DENTAL, LATITUDE, VISION_ID } from 'core/constants/products'
import getBadgeDental from 'core/controllerPlans/getBadgeDental'
import isAgentLogged from 'utils/pivot/agent/isAgentLogged'
import getPlanDetails from 'core/services/getPlanDetails'
import $ from 'jquery'
import getAdditionalInfo from 'ENROLL/add-ons/getAdditionalInfo'
import store from 'utils/store/withMiddleware'
import fetchJSONHandleError from 'utils/NET/fetchJSONHandleError'
import innerHTML from 'utils/DOM/innerHTML'
import { manageContent, manageDentalModal } from './managersV2'
import forEach from 'utils/DOM/forEach'
import tooltipMessage from 'core/controllerPlans/tooltipMessage'
import { DETAILS } from 'sites/routes'
import toggleClass from 'utils/DOM/toggleClass'
import isArray from 'utils/validation/isArray'

type TemplateAction = <A, B>(payload: A, helpers: B) => string
type BenefitsSummary = { [key: string]: string }
type BenefitsSummaries = { [key: string]: BenefitsSummary }

const getPlansDetails = (addonPlanId: number, state: Pivot.State) => {
  const plans = [state.plans[addonPlanId], state.plans[VISION_ID]].filter(Boolean)
  return Promise.all(
    plans.map(plan => fetchJSONHandleError({ url: state.jsons[plan.jsonId] }))
  )
}

const loadDentalModalContent = async addonPlanId => {
  const getHTMLFromDentalModalTemplate: TemplateAction = Templater(
    getById('template-dental-modal-v2').innerText
  )

  const state = store.getState()
  const basePayload = getPayloadForAddonPlanTemplate(state.plans[addonPlanId], state)

  const plansDetails = await getPlansDetails(addonPlanId, state)
  const html = getHTMLFromDentalModalTemplate({ ...basePayload, plansDetails }, {})

  innerHTML('#dental-modal-v2 .dental__modal__v2__content', html)

  // applies the current state to the modal
  manageDentalModal(state)
}

export const showDentalModal = (addonPlanId: number) => {
  innerHTML('#dental-modal-v2 .dental__modal__v2__content', '')
  $('#dental-modal-v2').attr('data-plan-id', addonPlanId).modal('show')

  // loads the content asynchronously
  loadDentalModalContent(addonPlanId)
}

const showGeneratedContent = (state: Pivot.State) => {
  // make quotes__v2 container visible
  const quotesV2Container = getById('quotes__v2')
  quotesV2Container.classList.remove('d-none')

  hideLoading()
  executeWhenFinishToRender(state.hcStorage)
}

const extractFirstNumber = (text: string) => {
  return text.match(/\d+/)[0]
}
const extractPriceText = (text: string) => {
  return text.match(/\$\S+/)[0]
}

const getTooltipInfo = () => {
  return {
    patientAdvocacyServices:
      'Shop and compare healthcare services like you would for the best rate on a hotel. ' +
      'Use our patient advocates to answer questions, get the lowest pricing on a multitude of services ' +
      'and schedule doctor appointments. Can’t afford the final medical bill? Our advocates will help ' +
      'negotiate your bill down.'
  }
}

const getBasicBenefitsSummary = (plan: Plan): BenefitsSummary => {
  const { physicianOfficeCopay, hospitalAdmissionSicknessInjury } = plan.coreBenefits

  return {
    deductible: '$0 Deductible',
    visit: `${extractPriceText(physicianOfficeCopay)} for doctor visits`,
    hospitalAdmission: `${hospitalAdmissionSicknessInjury} for hospital admission`,
    patientAdvocacyServices: 'Patient Advocacy Services',
    telemedicine: 'Free Unlimited Telemedicine'
  }
}

const getEmergencyBenefitText = (planId, extraDetails) => {
  const EMERGENCY = 'Emergency Room'

  const rawText = extraDetails[planId]?.plan
    ?.find(({ headline }) => headline === EMERGENCY)
    ?.body.find(({ title }) => title === EMERGENCY)
    ?.description[0]?.description.replaceAll('<br>', ', ')
    .trim()

  return `Emergency Room - ${rawText ?? '---'}`
}

const getInpatientPhysicianBenefitText = (planId: number, extraDetails) => {
  const rawText = extraDetails[planId]?.plan
    ?.find(({ headline }) => headline === 'Hospital Services')
    ?.body.find(({ title }) => title.includes('Inpatient Physician Visit'))
    ?.description[0]?.description.replaceAll('<br>', ', ')
    .trim()

  if (rawText === undefined) return '---'

  return `Inpatient physician visit - ${rawText}`
}

const getSubstanceAbuseBenefit = (planId: number, extraDetails) => {
  const rawText = extraDetails[planId]?.plan
    ?.find(({ headline }) => headline === 'Mental Health')
    ?.body.find(({ title }) => title.includes('Inpatient Substance Abuse'))
    ?.description[0]?.description.replaceAll('<br>', ', ')
    .trim()

  if (rawText === undefined) return '---'

  return `Inpatient substance abuse - ${rawText}`
}

const getConfinement = (plan: Plan) => {
  const {
    hospitalConfinementMedicalCopay,
    hospitalConfinementMedicalMax,
    hospitalConfinementInjury,
    hospitalConfinementSickness
  } = plan.coreBenefits

  const injuryPrice =
    hospitalConfinementInjury && extractPriceText(hospitalConfinementInjury)
  const sicknessPrice =
    hospitalConfinementSickness && extractPriceText(hospitalConfinementSickness)

  let copay
  if (injuryPrice || sicknessPrice) {
    if (injuryPrice === sicknessPrice) {
      copay = `${injuryPrice} per day`
    } else {
      copay = `${hospitalConfinementInjury}/${hospitalConfinementSickness}`
    }
  } else {
    copay = hospitalConfinementMedicalCopay.toLowerCase()
  }

  return `Hospital confinement - ${copay} (${hospitalConfinementMedicalMax} per confinement)`
}

const getBenefitsSummaries = (plan: Plan, extraDetails) => {
  const {
    physicianOfficeMax,
    wellnessBenefitCopay,
    wellnessBenefitMax,
    genericBrandNamePrescription,
    genericBrandNamePrescriptionMax,
    hospitalAdmissionSicknessInjury
  } = plan.coreBenefits

  const wellnessPriceText = extractPriceText(wellnessBenefitCopay)

  return {
    'Doctor visits': {
      physicianOfficeMax: `Doctor office visits - ${physicianOfficeMax}`,
      wellness: `${wellnessPriceText} wellness benefit per day - ${wellnessBenefitMax}`
    },
    'Prescription drugs': {
      prescription: genericBrandNamePrescriptionMax || genericBrandNamePrescription
    },
    'Hospital services': {
      admission: `Hospital Admission - Injury/Sickness ${hospitalAdmissionSicknessInjury} per confinement`,
      confinement: getConfinement(plan),
      emergency: getEmergencyBenefitText(plan.planId, extraDetails),
      inpatientPhysician: getInpatientPhysicianBenefitText(plan.planId, extraDetails),
      substanceAbuse: getSubstanceAbuseBenefit(plan.planId, extraDetails)
    },
    'Additional benefits*': {
      telemedicine: 'FREE and unlimited telemedicine',
      dermatology: 'FREE dermatology consultations',
      note: '*Not associated with Standard Life and Insurance Company Coverages'
    }
  }
}

/**
 * Previously addon selection was saved per plan, with the new design this is not longer the case; an addon can be
 * selected independently of the selected main plan, in order to remain compatible with the older version and other
 * functionalities (continue button, mail, etc.) the `addonsByPlan` state's property should be updated as well.

 * @param isVisionSelectedByAddonPlanId
 * @param plansFixed
 * @param selectedAddonPlanId
 */
export const getAddonsByPlanFromSelectedAddon = (
  plansFixed: number[],
  isVisionSelectedByAddonPlanId: { [addonPlanId: string]: boolean },
  selectedAddonPlanId?: number
) => {
  const addonPlanIds = [
    selectedAddonPlanId,
    isVisionSelectedByAddonPlanId[selectedAddonPlanId] && VISION_ID
  ].filter(Boolean)

  const entries = plansFixed.map(id => [id, addonPlanIds])
  return Object.fromEntries(entries)
}

const getAllBenefitSummaries = (plan: Plan, extraDetails) => {
  const benefitsSummaries = getBenefitsSummaries(plan, extraDetails)
  return {
    basic: getBasicBenefitsSummary(plan),
    ...benefitsSummaries
  }
}

const compareSummariesDifferences = (
  main: BenefitsSummaries,
  others: BenefitsSummaries[]
) => {
  const entries = Object.entries(main).map(([summaryName, benefitsSummary]) => {
    const diffSummary = Object.fromEntries(
      Object.entries(benefitsSummary).map(([key, text]) => {
        const isRepeated = others.every(other => other[summaryName][key] === text)
        const isDifferent = !isRepeated
        return [key, isDifferent]
      })
    )
    return [summaryName, diffSummary]
  })
  return Object.fromEntries(entries)
}

const getSummariesDifferences = (plan: Plan, state: Pivot.State, extraDetails) => {
  const { plansFixed, plans } = state

  const benefitSummaries = getAllBenefitSummaries(plan, extraDetails)

  const otherSummaries = plansFixed
    .filter(id => id !== plan.planId)
    .map(id => getAllBenefitSummaries(plans[id], extraDetails))

  return compareSummariesDifferences(benefitSummaries, otherSummaries)
}

export const getPayloadForPlanTemplate = (
  planId: number,
  state: Pivot.State,
  extraDetails
) => {
  const { plans, carriers, brochures } = state

  const plan = plans[planId]
  const carrier = carriers[plan.carrierId]
  const brochure = brochures[plan.brochureId]
  const monthlyCost = plan.durations[0].monthly

  const computed = {
    recommended: plan.planName.toLowerCase().includes('anchor select'),
    price: moneyFormat(monthlyCost.premium),
    basicBenefitsSummary: getBasicBenefitsSummary(plan),
    benefitsSummaries: extraDetails && getBenefitsSummaries(plan, extraDetails),
    canSaveQuotes: isAgentLogged(),
    isDifferentMap: extraDetails && getSummariesDifferences(plan, state, extraDetails),
    tooltipInfo: getTooltipInfo(),
    priceTooltip: tooltipMessage(state, plan, monthlyCost),
    detailsLink: `${DETAILS}?planid=${plan.planId}`
  }

  return { plan, carrier, computed, brochure }
}

const renderPlans = (state: Pivot.State, extraDetails) => {
  const getHTMLFromPlanTemplate: TemplateAction = Templater(
    getById('template-plan-v2').innerText
  )

  const planListNode = getById('plan-list-v2')

  const { plansFixed } = state

  planListNode.innerHTML = plansFixed
    .map(planId => getPayloadForPlanTemplate(planId, state, extraDetails))
    .map(getHTMLFromPlanTemplate)
    .join('')
}

const getPayloadForAddonPlanTemplate = (addonPlan: Plan, state: Pivot.State) => {
  const { brochures, hcStorage, plans } = state
  const { premium } = addonPlan.durations[0].monthly

  const visionPlan = plans[VISION_ID]

  const priceText = moneyFormat(premium)
  const visionPrice = visionPlan?.durations[0].monthly.premium || 0
  const priceWithVisionText = moneyFormat(premium + visionPrice)
  const brochure = brochures[addonPlan.brochureId]

  const modalPlans = [addonPlan, visionPlan].filter(Boolean)
  const modalInstructions = `Review the information below about Brilliant Dental&trade; 
    ${visionPlan ? 'and Vision' : ''} Insurance.`

  const computed = {
    recommended: addonPlan.planName.includes('Value'),
    title: addonPlan.planName.replaceAll('™', '<span class="tm">™</span>'),
    subtitle: getBadgeDental(addonPlan.planName, hcStorage.members).message,
    price: priceText,
    priceWithVision: priceWithVisionText,
    hasVision: Boolean(visionPlan),
    visionPrice: moneyFormat(visionPrice),
    isValue: addonPlan.planName.includes('Value'),
    modalInstructions,
    modalAddButtonTexts: [
      `Add Dental for ${priceText}/mo`,
      `Add Dental + Vision for ${priceWithVisionText}/mo`
    ],
    modalRemoveButtonTexts: [`Remove Dental Plan`, `Remove Dental + Vision Plan`],
    modalVisionValues: ['0', `1`]
  }

  return { plan: addonPlan, computed, modalPlans, brochure }
}

const compareMonthlyPrice = (planA, planB) => {
  return planA.durations[0].monthly.premium < planB.durations[0].monthly.premium ? -1 : 1
}

const showActiveAddonBanner = () => {
  const addonActiveContainer = getById('quotes__v2__addon__active')
  addonActiveContainer.classList.remove('d-none')
}

const renderAddons = (state: Pivot.State) => {
  const addonsSectionContainer = getById('quotes__v2__addons__section')
  addonsSectionContainer.classList.remove('d-none')

  const getHTMLFromAddonPlanTemplate: TemplateAction = Templater(
    getById('template-plan-addon-v2').innerText
  )

  const addonPlanListNode = getById('addon-plan-list-v2')

  const { plansByType, plans } = state

  const addonPlanIds = plansByType[DENTAL] || []

  addonPlanListNode.innerHTML = addonPlanIds
    .map(addonPlanId => plans[addonPlanId])
    .sort(compareMonthlyPrice)
    .map(addonPlan => getPayloadForAddonPlanTemplate(addonPlan, state))
    .map(getHTMLFromAddonPlanTemplate)
    .join('')
}

const checkToRenderAddons = (state: Pivot.State) => {
  const { hasVisionActive, hasDentalActive, plansByType, hcStorage } = state
  if (hasDentalActive || hasVisionActive) {
    showActiveAddonBanner()
    return
  }
  const shouldRender = plansByType[DENTAL]?.length && !hcStorage.childOnly

  if (!shouldRender) {
    return
  }

  renderAddons(state)
}

const renderExtraDetails = (state: Pivot.State) => {
  const { brochures, plansByType, plans } = state
  const dentalPlanId = plansByType[DENTAL]?.[0]
  const visionPlan = plans[VISION_ID]

  const dentalPlans = plansByType[DENTAL]
  const latitudePlans = plansByType[LATITUDE]

  const brochure = brochures[plans[dentalPlanId]?.brochureId]
  const brochureNode: HTMLAnchorElement = getById('quotes__v2__content__brochure')
  brochureNode.href = brochure

  forEach('.quotes__v2__vision__label', node => {
    if (!visionPlan) {
      node.classList.add('d-none')
    }
  })

  if (!isAgentLogged()) {
    const mailsButton = getById('quotes__v2__header__mails__button')
    mailsButton.classList.add('d-none')
  }

  toggleClass(
    '.disclaimer__dental',
    'd-none',
    !(isArray(dentalPlans) && dentalPlans.length > 0)
  )
  toggleClass(
    '.disclaimer__latitude-large',
    'd-none',
    !(isArray(latitudePlans) && latitudePlans.length > 0)
  )
}

const fetchExtraDetails = async ({ plansFixed, hcStorage }: Pivot.State) => {
  const promises = plansFixed.map(planId =>
    getPlanDetails(planId, hcStorage.location.state_acronym)
  )
  const extraDetails = await Promise.all(promises)

  const entries = plansFixed.map((planId, index) => [planId, extraDetails[index]])

  return Object.fromEntries(entries)
}

export const renderPageContentV2 = async (state: Pivot.State) => {
  const extraDetails = await fetchExtraDetails(state)
  await getAdditionalInfo(state)

  renderPlans(state, extraDetails)
  checkToRenderAddons(state)
  renderExtraDetails(state)

  manageContent(state)
  showGeneratedContent(state)
}
